/**
 * Tenant Utilities
 * Handles tenant ID conversion and validation
 */

import { supabaseAdmin } from '../supabase/clients.js';
import { ServiceError, ErrorCodes } from './errorHandler.js';

/**
 * Convert text tenant ID (e.g., 'tn_01') to UUID
 */
export const getTenantUuid = async (textTenantId) => {
  try {
    const { data, error } = await supabaseAdmin
      .from('tenants')
      .select('id')
      .eq('tenant_id', textTenantId)
      .single();

    if (error || !data) {
      throw new ServiceError(
        `Tenant not found: ${textTenantId}`,
        ErrorCodes.TENANT_NOT_FOUND,
        { textTenantId }
      );
    }

    return data.id;
  } catch (error) {
    if (error instanceof ServiceError) {
      throw error;
    }
    throw new ServiceError(
      `Failed to get tenant UUID: ${error.message}`,
      ErrorCodes.TENANT_NOT_FOUND,
      { textTenantId, originalError: error }
    );
  }
};

/**
 * Convert UUID tenant ID to text format
 */
export const getTenantText = async (uuidTenantId) => {
  try {
    const { data, error } = await supabaseAdmin
      .from('tenants')
      .select('tenant_id')
      .eq('id', uuidTenantId)
      .single();

    if (error || !data) {
      throw new ServiceError(
        `Tenant not found: ${uuidTenantId}`,
        ErrorCodes.TENANT_NOT_FOUND,
        { uuidTenantId }
      );
    }

    return data.tenant_id;
  } catch (error) {
    if (error instanceof ServiceError) {
      throw error;
    }
    throw new ServiceError(
      `Failed to get tenant text ID: ${error.message}`,
      ErrorCodes.TENANT_NOT_FOUND,
      { uuidTenantId, originalError: error }
    );
  }
};

/**
 * Get tenant UUID and validate it exists
 */
export const validateAndGetTenantUuid = async (textTenantId) => {
  if (!textTenantId) {
    throw new ServiceError(
      'Tenant ID is required',
      ErrorCodes.TENANT_REQUIRED,
      { textTenantId }
    );
  }

  // Validate format
  if (!/^tn_\d{2}$/.test(textTenantId)) {
    throw new ServiceError(
      'Invalid tenant ID format. Expected format: tn_XX',
      ErrorCodes.TENANT_INVALID,
      { textTenantId }
    );
  }

  return await getTenantUuid(textTenantId);
};

/**
 * Cache for tenant UUID mappings
 */
const tenantUuidCache = new Map();

/**
 * Get tenant UUID with caching
 */
export const getTenantUuidCached = async (textTenantId) => {
  if (tenantUuidCache.has(textTenantId)) {
    return tenantUuidCache.get(textTenantId);
  }

  const uuid = await getTenantUuid(textTenantId);
  tenantUuidCache.set(textTenantId, uuid);
  return uuid;
};

/**
 * Clear tenant cache
 */
export const clearTenantCache = () => {
  tenantUuidCache.clear();
};
